import React, { useState } from 'react';
import { format } from 'date-fns';
import { Article } from '../../../contexts/ArticlesContext';
import { Modal } from '../../common/Modal';
import { useClipboard } from '../../../hooks/useClipboard';
import { HotkeyConfig } from '../../../hooks/useHotkeys';
import { LanguageSelector } from '../../common/LanguageSelector';

interface ArticleDetailModalProps {
  article: Article;
  onClose: () => void;
}

export function ArticleDetailModal({ article, onClose }: ArticleDetailModalProps) {
  const [isReScraping, setIsReScraping] = useState(false);
  const [reScrapedContent, setReScrapedContent] = useState<string | null>(null);
  const [reScrapingError, setReScrapingError] = useState<string | null>(null);
  const [selectedLanguage, setSelectedLanguage] = useState<string>('en');
  const { copied: urlCopied, copyToClipboard } = useClipboard();

  // Helper function to get multilingual content
  const getMultilingualContent = (content: any, fallback: string = ''): string => {
    if (!content) return fallback;
    
    // If content is a string, return it directly
    if (typeof content === 'string') return content;
    
    // If content is an object with language keys, get the selected language
    if (typeof content === 'object' && content !== null) {
      return content[selectedLanguage] || content['en'] || Object.values(content)[0] || fallback;
    }
    
    return fallback;
  };

  // Helper function to get multilingual array content
  const getMultilingualArray = (content: any): string[] => {
    if (!content) return [];
    
    // If content is an array, return it directly
    if (Array.isArray(content)) return content;
    
    // If content is an object with language keys, get the selected language
    if (typeof content === 'object' && content !== null) {
      const langContent = content[selectedLanguage] || content['en'] || Object.values(content)[0];
      return Array.isArray(langContent) ? langContent : [];
    }
    
    return [];
  };

  // Get available languages from the analysis
  const getAvailableLanguages = (): string[] => {
    if (!article.latestAnalysis) return ['en'];
    
    const analysis = article.latestAnalysis;
    const languages = new Set<string>();
    
    // Check various fields for available languages
    [analysis.summary, analysis.keyPoints, analysis.reasoning?.overall].forEach(field => {
      if (field && typeof field === 'object' && field !== null) {
        Object.keys(field).forEach(lang => languages.add(lang));
      }
    });

    // Also consider reasoning.byCategory nested maps (values may be multilingual objects)
    const byCat: any = (analysis.reasoning as any)?.byCategory;
    if (byCat && typeof byCat === 'object') {
      Object.values(byCat as Record<string, unknown>).forEach((val) => {
        if (val && typeof val === 'object' && !Array.isArray(val)) {
          Object.keys(val as Record<string, unknown>).forEach((lang) => languages.add(lang));
        }
      });
    }
    
    return languages.size > 0 ? Array.from(languages) : ['en'];
  };

  const availableLanguages = getAvailableLanguages();

  const handleReScrape = async () => {
    if (!article.source.requiresScrapingForAnalysis) {
      setReScrapingError('This source does not support full content scraping');
      return;
    }

    setIsReScraping(true);
    setReScrapingError(null);
    setReScrapedContent(null);

    try {
      // Note: This endpoint doesn't exist yet - would need to be implemented on the server
      // For now, we'll show a placeholder implementation
      setReScrapingError('Re-scraping endpoint not yet implemented. This would fetch the full article content using the source\'s CSS selectors.');
    } catch (error) {
      console.error('Re-scraping failed:', error);
      setReScrapingError(error instanceof Error ? error.message : 'Failed to re-scrape content');
    } finally {
      setIsReScraping(false);
    }
  };

  // Define hotkeys for this modal
  const hotkeys: HotkeyConfig[] = [
    {
      key: 'o',
      description: 'Open URL',
      action: () => window.open(article.url, '_blank', 'noopener,noreferrer')
    },
    {
      key: 'c',
      description: urlCopied ? 'Copied!' : 'Copy URL',
      action: () => copyToClipboard(article.url)
    },
    ...(article.source.requiresScrapingForAnalysis ? [{
      key: 'r',
      description: 'Re-scrape',
      action: handleReScrape,
      condition: () => !isReScraping
    }] : [])
  ];

  return (
    <Modal
      isOpen={true}
      onClose={onClose}
      title={article.title}
      hotkeys={hotkeys}
      maxWidth="4xl"
    >
      <div className="space-y-4">
        <div className="text-sm tc-text-secondary space-y-2">
            <p><strong>Article ID:</strong> {article.id}</p>
            <p><strong>Source:</strong> {article.source.name}</p>
            <p><strong>URL:</strong> <a href={article.url} target="_blank" rel="noopener noreferrer" className="tc-link">{article.url}</a></p>
            <p><strong>Published:</strong> {article.publishedAt ? format(new Date(article.publishedAt), 'PPpp') : 'Unknown'}</p>
            <p><strong>Scraped:</strong> {format(new Date(article.scrapedAt), 'PPpp')}</p>
            <p><strong>Scraping for Analysis:</strong> {article.source.requiresScrapingForAnalysis ? 'Enabled' : 'Disabled'}</p>
            <p><strong>Scraping for Relevance:</strong> {article.source.requiresScrapingForRelevance ? 'Enabled' : 'Disabled'}</p>
          </div>

          {/* RSS Content - What AI Analyzer Sees */}
          <div className="border rounded-lg p-4 bg-gray-50">
            <div className="flex items-center justify-between mb-2">
              <h4 className="font-medium tc-text-primary">
                {article.source.requiresScrapingForAnalysis 
                  ? 'Stored RSS Content (AI fetches full article during analysis)'
                  : 'RSS Content (What AI Analyzer Sees)'}
              </h4>
              {article.source.requiresScrapingForAnalysis && (
                <button
                  onClick={handleReScrape}
                  disabled={isReScraping}
                  className="tc-btn tc-btn-sm tc-btn-primary"
                  title="Re-scrape the full article content to see what the AI analyzer would see"
                >
                  {isReScraping ? '🔄 Scraping...' : '🔄 Re-scrape Content'}
                </button>
              )}
            </div>
            
            <div className="text-sm tc-text-secondary space-y-2">
              <p><strong>Content Length:</strong> {article.content?.length || 0} characters</p>
              {article.extractionFailed && (
                <div className="bg-red-50 border border-red-200 rounded p-3 mb-2">
                  <p className="text-sm text-red-600 font-medium">⚠️ Full content extraction failed!</p>
                  <p className="text-xs text-red-500 mt-1">{article.extractionError || 'Unknown error'}</p>
                  <p className="text-xs text-red-500 mt-1">Check the CSS selectors in the source configuration.</p>
                </div>
              )}
              
              {/* Original RSS Content */}
              <div className="border rounded p-3 bg-white">
                <h5 className="font-medium text-sm mb-2">Original RSS Content:</h5>
                <p className="text-sm">{article.content || 'No content available'}</p>
              </div>
              
              {/* Re-scraped Content */}
              {reScrapedContent && (
                <div className="border rounded p-3 bg-green-50">
                  <h5 className="font-medium text-sm mb-2">Re-scraped Full Content:</h5>
                  <p className="text-sm">{reScrapedContent}</p>
                </div>
              )}
              
              {/* Re-scraping Error */}
              {reScrapingError && (
                <div className="bg-red-50 border border-red-200 rounded p-3">
                  <p className="text-sm text-red-600 font-medium">⚠️ Re-scraping failed!</p>
                  <p className="text-xs text-red-500 mt-1">{reScrapingError}</p>
                </div>
              )}
              
              <p className="text-xs text-gray-500">
                {article.source.requiresScrapingForAnalysis 
                  ? 'This source has full content scraping enabled for analysis. AI will attempt to get the full article.'
                  : 'This source only uses RSS snippets for analysis. Consider enabling full scraping if content is too short.'}
              </p>
            </div>
          </div>

          {/* Analysis Results */}
          {article.latestAnalysis && (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h4 className="font-medium tc-text-primary">AI Risk Analysis</h4>
                <div className="flex items-center gap-3">
                  <LanguageSelector
                    availableLanguages={availableLanguages}
                    selectedLanguage={selectedLanguage}
                    onLanguageChange={setSelectedLanguage}
                  />
                  <span className="text-xs tc-text-secondary">
                    Analyzed: {format(new Date(article.latestAnalysis.processedAt), 'PPp')}
                  </span>
                </div>
              </div>
              <div className="space-y-4">
                {/* Risk Assessment Header */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div>
                        <span className="font-medium">Overall Risk:</span>
                        <span className="ml-2 font-bold text-lg">{article.latestAnalysis.overallRiskScore}/10</span>
                      </div>
                      <span className={`px-2 py-1 rounded text-xs font-bold ${
                        article.latestAnalysis.alertLevel === 'CRITICAL' ? 'bg-red-500 text-white' :
                        article.latestAnalysis.alertLevel === 'HIGH' ? 'bg-orange-500 text-white' :
                        article.latestAnalysis.alertLevel === 'MEDIUM' ? 'bg-yellow-500 text-black' :
                        'bg-green-500 text-white'
                      }`}>
                        {article.latestAnalysis.alertLevel}
                      </span>
                    </div>
                    {article.latestAnalysis.confidenceLevel !== undefined && (
                      <span className="text-sm tc-text-secondary bg-white px-2 py-1 rounded">
                        Confidence: {Math.round(article.latestAnalysis.confidenceLevel * 100)}%
                      </span>
                    )}
                  </div>
                </div>
                
                {/* AI Key Points */}
                {article.latestAnalysis.keyPoints && getMultilingualArray(article.latestAnalysis.keyPoints).length > 0 && (
                  <div>
                    <h5 className="font-medium mb-2">🎯 Key Analysis Points:</h5>
                    <ul className="list-disc pl-5 space-y-1 text-sm tc-text-secondary">
                      {getMultilingualArray(article.latestAnalysis.keyPoints).map((point, index) => (
                        <li key={index}>{point}</li>
                      ))}
                    </ul>
                  </div>
                )}
                
                {/* Key Entities */}
                {article.latestAnalysis.keyEntities && article.latestAnalysis.keyEntities.length > 0 && (
                  <div>
                    <h5 className="font-medium mb-2">🏛️ Key Entities:</h5>
                    <div className="flex flex-wrap gap-1">
                      {article.latestAnalysis.keyEntities.map((entity, index) => (
                        <span key={index} className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-sm">
                          {entity}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                
                <div>
                  <h5 className="font-medium mb-2">📊 Category Scores:</h5>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    {Object.entries(article.latestAnalysis.categoryScores).map(([category, score]) => (
                      <div key={category} className="flex justify-between">
                        <span className="tc-text-secondary">
                          {category.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, l => l.toUpperCase())}:
                        </span>
                        <span className="font-medium">{score}/10</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              
              {article.latestAnalysis.threatActorMentions && article.latestAnalysis.threatActorMentions.length > 0 && (
                <div className="text-sm tc-text-secondary">
                  <h5 className="font-medium mb-1">Threat Actors:</h5>
                  <p>{article.latestAnalysis.threatActorMentions.join(', ')}</p>
                </div>
              )}
              
              {/* AI Reasoning */}
              {article.latestAnalysis.reasoning && (
                <div className="mt-4">
                  <h5 className="font-medium mb-2">🧠 AI Reasoning:</h5>
                  <div className="bg-gray-50 border rounded p-3 text-sm tc-text-secondary">
                    {(() => {
                      const r: any = article.latestAnalysis.reasoning;
                      if (!r) return <p className="text-gray-500 italic">No detailed reasoning available</p>;
                      if (typeof r !== 'object') return <p>{getMultilingualContent(r, 'No reasoning available')}</p>;
                      const rows: React.ReactNode[] = [];
                      // overall
                      if (r.overall) {
                        rows.push(
                          <div key="overall">
                            <span className="font-medium text-gray-700">overall:</span>
                            <span className="ml-2">{getMultilingualContent(r.overall, 'No reasoning available')}</span>
                          </div>
                        );
                      }
                      // byCategory — show only selected language string per category
                      if (r.byCategory && typeof r.byCategory === 'object') {
                        const cats = Object.entries(r.byCategory as Record<string, any>);
                        if (cats.length) {
                          rows.push(
                            <div key="byCategory" className="mt-2">
                              <div className="font-medium text-gray-700 mb-1">byCategory:</div>
                              <div className="space-y-1">
                                {cats.map(([cat, val]) => {
                                  let text: string = '';
                                  if (val && typeof val === 'object' && !Array.isArray(val)) {
                                    text = (val as any)[selectedLanguage] || (val as any)['en'] || Object.values(val as any)[0] || '';
                                  } else if (typeof val === 'string') {
                                    text = val;
                                  }
                                  return (
                                    <div key={cat} className="flex">
                                      <span className="font-medium mr-2">{cat}:</span>
                                      <span className="flex-1">{text || '—'}</span>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          );
                        }
                      }
                      if (!rows.length) return <p className="text-gray-500 italic">No detailed reasoning available</p>;
                      return <div className="space-y-2">{rows}</div>;
                    })()}
                  </div>
                </div>
              )}
              
              {article.latestAnalysis.summary && (
                <div className="mt-4">
                  <h5 className="font-medium mb-2">📝 AI Summary:</h5>
                  <p className="tc-text-secondary text-sm">
                    {getMultilingualContent(article.latestAnalysis.summary, 'No summary available')}
                  </p>
                </div>
              )}
            </div>
          )}
      </div>
    </Modal>
  );
}
